import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import { SalesTargetService, currentDhakaMonth } from '../../services/salesTarget.service';
import {
  SalesTargetMonthView, SalesTargetRepRow, SalesTargetSubject, SalesTargetTeamRow,
} from '../../interfaces/sales/salesTarget.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { formatMoney } from '../../utils/format';
import { helpWhy } from '../../help/why';

const pct = (n: number | null) => (n === null || n === undefined ? '-' : `${n.toFixed(1)}%`);

/** One editable target cell: type an amount and Save, or Clear to remove it. Read-only when the viewer cannot manage. */
const TargetEditor: React.FC<{
  value: number | null; targetId: string | null; canManage: boolean; busy: boolean; label: string;
  onSave: (amount: number) => Promise<void>; onClear: (id: string) => Promise<void>;
}> = ({ value, targetId, canManage, busy, label, onSave, onClear }) => {
  const [text, setText] = useState(value === null || value === undefined ? '' : String(value));
  useEffect(() => { setText(value === null || value === undefined ? '' : String(value)); }, [value]);
  if (!canManage) return <>{value === null ? '-' : formatMoney(value)}</>;
  const parsed = Number(text);
  const valid = text.trim() !== '' && Number.isFinite(parsed) && parsed >= 0;
  const changed = valid && parsed !== value;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
      <TextField
        size="small" data-help="sales.targets.amount" type="number" label={label} value={text} onChange={e => setText(e.target.value)}
        sx={{ flex: '1 1 120px', maxWidth: 180 }} inputProps={{ min: 0, step: '0.01' }}
      />
      <Button data-help="sales.targets.save" {...helpWhy(busy ? null : !valid ? { kind: 'status', message: 'Enter a target of zero or more.' } : !changed ? { kind: 'status', message: 'The amount has not changed yet.' } : null)} size="small" variant="contained" disabled={!changed || busy} onClick={() => onSave(parsed)}>Save</Button>
      {targetId && <Button data-help="sales.targets.clear" size="small" color="inherit" disabled={busy} onClick={() => onClear(targetId)}>Clear</Button>}
    </Box>
  );
};

/** S2: monthly sales targets against actuals for the people and teams in the viewer's sales scope. */
const SalesTargets: React.FC = () => {
  const toast = useToast();
  const [month, setMonth] = useState(currentDhakaMonth());
  const [view, setView] = useState<SalesTargetMonthView | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const institutionId = AuthService.getSelectedInstitution();

  const load = useCallback(async () => {
    if (!institutionId) { setLoading(false); return; }
    setLoading(true);
    try {
      setView(await SalesTargetService.month(institutionId, month));
    } catch (e) {
      toast.showApiError(e, 'Failed to load sales targets');
    } finally {
      setLoading(false);
    }
  }, [institutionId, month, toast]);

  useEffect(() => { load(); }, [load]);

  const save = async (subject: SalesTargetSubject, id: string, amount: number, keep: { targetQuantity: number | null; note: string | null }) => {
    if (!institutionId) return;
    setBusy(true);
    try {
      await SalesTargetService.upsert(institutionId, {
        month: `${month}-01`, subjectType: subject, amount,
        userId: subject === 'REP' ? id : null, teamId: subject === 'TEAM' ? id : null,
        quantity: keep.targetQuantity, note: keep.note,
      });
      toast.showSuccess('Target saved');
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to save the target');
    } finally {
      setBusy(false);
    }
  };

  const clear = async (targetId: string) => {
    setBusy(true);
    try {
      await SalesTargetService.remove(targetId);
      toast.showSuccess('Target cleared');
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to clear the target');
    } finally {
      setBusy(false);
    }
  };

  const canManage = !!view?.canManage;

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }} data-help-page="sales.targets.page">
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>Sales Targets</Typography>

      <Box data-help-section="sales.targets.month" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField
          size="small" data-help="sales.targets.monthPick" type="month" label="Month" value={month} onChange={e => e.target.value && setMonth(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 180px', maxWidth: 240 }}
        />
      </Box>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>}

      {!loading && view && (
        <>
          <Paper data-help-section="sales.targets.summary" variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box><Typography variant="caption" color="text.secondary">Target</Typography><Typography variant="h6">{formatMoney(view.target)}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Confirmed orders (gross), teams</Typography><Typography variant="h6">{formatMoney(view.actual)}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Achievement</Typography><Typography variant="h6">{pct(view.achievementPct)}</Typography></Box>
              {view.outsideTeamsActual > 0 && (
                <Box><Typography variant="caption" color="text.secondary">Sales outside teams</Typography><Typography variant="h6">{formatMoney(view.outsideTeamsActual)}</Typography></Box>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Confirmed orders (gross) is the full value of confirmed sales orders by order date, including short-closed orders at their confirmed value. The headline counts team-attributed sales only, the same population as the targets. A team with its own target counts that figure; otherwise the sum of its people's targets.
            </Typography>
          </Paper>

          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Teams</Typography>
          <TableContainer component={Paper} data-help-section="sales.targets.teams" variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell data-help="sales.targets.teams.col.team">Team</TableCell><TableCell align="right" data-help="sales.targets.teams.col.people">People</TableCell><TableCell data-help="sales.targets.teams.col.target">Target</TableCell>
                  <TableCell align="right" data-help="sales.targets.teams.col.fromPeople">From people</TableCell><TableCell align="right" data-help="sales.targets.teams.col.confirmed">Confirmed (gross)</TableCell><TableCell align="right" data-help="sales.targets.teams.col.achievement">Achievement</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {view.teams.length === 0 && <TableRow><TableCell colSpan={6}>No teams in your scope.</TableCell></TableRow>}
                {view.teams.map((t: SalesTargetTeamRow) => (
                  <TableRow key={t.teamId}>
                    <TableCell>{t.name}</TableCell>
                    <TableCell align="right">{t.memberCount}</TableCell>
                    <TableCell>
                      <TargetEditor
                        value={t.target} targetId={t.targetId} canManage={canManage} busy={busy} label={`${t.name} target`}
                        onSave={a => save('TEAM', t.teamId, a, { targetQuantity: t.targetQuantity, note: t.note })} onClear={clear}
                      />
                    </TableCell>
                    <TableCell align="right">{formatMoney(t.repTargetsSum)}</TableCell>
                    <TableCell align="right">{formatMoney(t.actual)}</TableCell>
                    <TableCell align="right">{pct(t.achievementPct)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>People</Typography>
          <TableContainer component={Paper} data-help-section="sales.targets.people" variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell data-help="sales.targets.people.col.person">Salesperson</TableCell><TableCell data-help="sales.targets.people.col.team">Team</TableCell><TableCell data-help="sales.targets.people.col.target">Target</TableCell>
                  <TableCell align="right" data-help="sales.targets.people.col.confirmed">Confirmed (gross)</TableCell><TableCell align="right" data-help="sales.targets.people.col.orders">Orders</TableCell><TableCell align="right" data-help="sales.targets.people.col.achievement">Achievement</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {view.reps.length === 0 && <TableRow><TableCell colSpan={6}>No salespeople in your scope.</TableCell></TableRow>}
                {view.reps.map((r: SalesTargetRepRow) => (
                  <TableRow key={r.userId}>
                    <TableCell>{r.name ?? 'Unknown'}</TableCell>
                    <TableCell>{r.teamName ?? '-'}</TableCell>
                    <TableCell>
                      <TargetEditor
                        value={r.target} targetId={r.targetId} canManage={canManage && r.editable} busy={busy} label={`${r.name ?? 'Person'} target`}
                        onSave={a => save('REP', r.userId, a, { targetQuantity: r.targetQuantity, note: r.note })} onClear={clear}
                      />
                    </TableCell>
                    <TableCell align="right">{formatMoney(r.actual)}</TableCell>
                    <TableCell align="right">{r.orders}</TableCell>
                    <TableCell align="right">{pct(r.achievementPct)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {!loading && !view && <Alert severity="warning">Sales targets are unavailable right now.</Alert>}
    </Box>
  );
};

export default SalesTargets;
